/**
 * Guided creation flow: an automation flow (Start → Send Message → End).
 *
 * Hangs off the Automations › Automations stop (the flow list + builder). The
 * user ends the flow having built a real flow — a Starting Step (already on
 * every new canvas), a Send Message node and an End node, wired together and
 * saved.
 *
 * ┌─ WHY NODES ARE "DROPPED", NOT DRAGGED, AND EDGES ARE "CONNECTED", NOT DRAWN ─┐
 * │ The canvas (`@xyflow/react`) uses two gestures neither of which is a click: │
 * │ adding a node is native HTML5 drag-and-drop (`dataTransfer`), and wiring two │
 * │ nodes together is the library's own pointer-tracking connection gesture     │
 * │ (pointerdown on a handle, then a document-level pointermove/pointerup). A   │
 * │ ring-and-callout walkthrough has no "press Next to continue this drag"      │
 * │ moment, so both are performed by the guide itself when the user presses     │
 * │ this step's Next — exactly the same trade the rest of this module makes for │
 * │ an ordinary click (see `TaskStep.onNext`), just extended to a gesture that   │
 * │ cannot be expressed as one. The user can still do either by hand first; both│
 * │ actions check `satisfied()` before doing anything and no-op if it is        │
 * │ already true.                                                               │
 * │                                                                            │
 * │ Both are best-effort. If a library internals change ever breaks the        │
 * │ synthetic gesture, `satisfied()` simply stays false, the step is recorded  │
 * │ as skipped (steps always advance — see `TaskEngine`), and the callout's own │
 * │ instructions are what carry the user through it by hand instead.           │
 * └────────────────────────────────────────────────────────────────────────────┘
 */

import { openAccordion, resolveAnchor, submenuAnchor, type AnchorSpec } from '../engine/anchors';
import {
  clickWhenReady,
  fillField,
  findVisible,
  isFilled,
  pressWhenReady,
  waitForAnchor,
  waitUntil,
} from './formFill';
import { closeFlowChrome } from './closeChrome';
import { flashAnchor } from './visibleAction';
import type { TaskFlow } from './types';

/**
 * Every route that renders the automations LIST.
 *
 * 🔴 `/home/automation` IS THE ONE THE SIDEBAR ACTUALLY USES, and leaving it out
 * is what made this whole flow look broken. `App.tsx` mounts `<Flows />` at BOTH
 * `automation` and `automation/flows`, and the sidebar row
 * (`AppSidebar.tsx`: `{ title: 'Automations', url: '/home/automation' }`) links
 * to the shorter one — so after `beforeStart` clicked that row, an exact-equality
 * check against `/home/automation/flows` was false on the page it had just
 * successfully opened.
 *
 * The cost was not a wrong highlight, it was TEN SECONDS OF NOTHING, twice over:
 * `beforeStart` burned its full 5s `waitUntil` before giving up, and then the
 * first step's `ready()` refused the Create automation button it could plainly
 * resolve for another 5s (`waitForTarget` will not accept a target `ready()`
 * disagrees with) — which is the reported "the guide is showing but nothing is
 * highlighted": the callout was on screen, unanchored, waiting on a predicate
 * that could never turn true. The ring only appeared afterwards because
 * `resolve()` does not consult `ready()`.
 *
 * `/home/automation/instagram` and `/home/automation/facebook` are DIFFERENT
 * lists (their own components, their own builders), which is why this is an
 * exact-match list rather than a `startsWith`.
 */
const FLOWS_LIST_PATHS = ['/home/automation', '/home/automation/flows'];

/** The sidebar row that opens the automations list. */
const SIDEBAR_ROW = submenuAnchor('Automations', 'Automations');

/**
 * The list page's "Create automation" button.
 *
 * 🔴 NOT SCOPED TO `AutomationsWorkspaceFilterBar`'s action bar — an earlier
 * version of this anchor was, and it silently failed the very first time this
 * flow runs: on an account with ZERO existing WhatsApp flows and no filters
 * applied, `Flows.tsx` hides that whole toolbar
 * (`shouldHideAutomationsFilterBar`) and renders a completely different
 * empty-state screen with its OWN "Create automation" button instead — same
 * label, different DOM, no shared container to scope on. With the narrower
 * anchor, `onFlowsListPage()` (which resolves this same spec) never turned
 * true, so `beforeStart`'s `waitUntil` burned its full 5s timeout, and then
 * the first step did the same — a many-second stall on every "Try it" click
 * for exactly the new-account case this flow exists to onboard.
 *
 * Matched by label alone instead. Safe here specifically because every use of
 * this anchor in this file is already gated by `onFlowsListPage()` (an exact
 * route check) — the concern a scoped anchor would normally guard against
 * (an identically-labelled button on the Instagram/Facebook automation
 * lists) is handled by the route check, not by DOM scoping.
 */
const CREATE_AUTOMATION_BUTTON: AnchorSpec = {
  structural: { within: 'body', match: 'button', labels: ['Create automation'] },
};

const NAME_INPUT_SELECTOR = '#new-automation-name';
const CONFIRM_CREATE_BUTTON: AnchorSpec = { selector: '[data-testid="create-automation-confirm"]' };

/**
 * The channel a new automation is created for.
 *
 * 🔴 "CREATE AUTOMATION" IS A MENU NOW, NOT A BUTTON THAT CREATES SOMETHING,
 * AND THAT IS WHAT BROKE THIS WHOLE FLOW. `/home/automation` hosts WhatsApp,
 * Instagram and Facebook behind one list, so `CreateAutomationMenu` asks which
 * channel first (`handleCreatePlatformSelected` in `Flows.tsx`) and only then
 * opens the name dialog. The step below used to press the button and wait for
 * `#new-automation-name`, which from that moment could never appear: every
 * step of the flow was recorded as skipped, the builder never opened, and the
 * walkthrough finished having built nothing.
 *
 * Matched by the words on the item inside whichever menu is open, exactly as
 * `createForm.ts` matches its own — the two `CreateAutomationMenu` instances
 * carry DIFFERENT test ids (`create-automation-menu-toolbar-whatsapp` on the
 * list's toolbar, `create-automation-menu-whatsapp` on the empty state), and
 * which one renders depends on whether the account has any flows yet. The
 * `role="menu"` / `role="menuitem"` pairing is Radix's own semantics and holds
 * for both.
 */
const WHATSAPP_CHANNEL_ITEM: AnchorSpec = {
  structural: { within: '[role="menu"]', match: '[role="menuitem"]', labels: ['WhatsApp'] },
};

/** Is the channel menu on screen? */
const channelMenuOpen = () => Boolean(resolveAnchor(WHATSAPP_CHANNEL_ITEM));

/** The palette's own draggable node rows, scoped to the palette panel. */
const PALETTE_SCOPE = '.border-r.bg-card';
const draggableLabelled = (label: string): AnchorSpec => ({
  structural: { within: PALETTE_SCOPE, match: '[draggable="true"]', labels: [label] },
});

const SAVE_BUTTON: AnchorSpec = {
  structural: { within: 'body', match: 'button', labels: ['Save'] },
};

/**
 * The toolbar's back arrow — `goBack` in `FlowToolbar.tsx`, navigates to the
 * automations list.
 *
 * Icon-only, no text and no `aria-label`, so a label-based anchor resolves to
 * nothing here (`accessibleLabel` falls through to an empty string). Matched
 * instead on the lucide-react icon's own class: every icon that library
 * renders is `lucide-<kebab-case-name>` (`createLucideIcon.js`), so
 * `lucide-arrow-left` is as stable an identifier as a `data-testid` would be,
 * without needing the app to add one.
 */
const BACK_TO_LIST_BUTTON: AnchorSpec = { selector: 'button:has(svg.lucide-arrow-left)' };

/**
 * The two buttons on the unsaved-changes guard the back arrow routes through
 * (`NavigationContext`).
 *
 * Scoped to the `alertdialog` they live in, which is the only place either ever
 * appears — and which keeps an unscoped label match from finding somebody
 * else's button on the page behind it.
 */
const DISCARD_CHANGES_BUTTON: AnchorSpec = {
  structural: { within: '[role="alertdialog"]', match: 'button', labels: ['Discard changes'] },
};
const KEEP_EDITING_BUTTON: AnchorSpec = {
  structural: { within: '[role="alertdialog"]', match: 'button', labels: ['Keep editing'] },
};

/**
 * The toolbar's "Run test" button — opens the validation panel that lists any
 * errors/warnings on the flow.
 *
 * Matched via `labelWithin` rather than the button's own full text: the
 * button grows a numeric badge next to the label once there are
 * errors/warnings (`Run test` + a count), which would break an exact-text
 * match on the whole button. The label lives on its own `<span>`, so this
 * checks THAT for an exact match and ignores whatever else (icon, badge) sits
 * beside it.
 */
const RUN_TEST_BUTTON: AnchorSpec = {
  structural: { within: 'body', match: 'button', labels: ['Run test'], labelWithin: 'span' },
};

/**
 * A validation issue's "Focus" button — jumps the canvas to the node it's
 * about and opens that node's own inspector panel (`FlowValidationPanel`'s
 * `onNodeFocus`). Every issue row grows one, all labelled identically, so
 * this resolves to whichever issue is FIRST in the panel's list — which in
 * this flow's own well-behaved path is also the ONLY one there (see the
 * `run-test` step). Absent entirely when the panel reports "No Issues", which
 * is the expected, common case once `fill-message` has already done its job.
 */
const FIRST_ISSUE_FOCUS_BUTTON: AnchorSpec = {
  structural: { within: 'body', match: 'button', labels: ['Focus'] },
};

/** The Send Message node's body-text field, opened in the inspector on drop/focus. */
const MESSAGE_FIELD_SELECTOR = '#message';

const DUMMY_MESSAGE = 'Hi! Thanks for reaching out 👋 We got your message and will reply shortly.';

/**
 * Is this pathname one of the automations-list routes?
 *
 * Exported and pure so the route family can be tested without a browser — the
 * bug this guards against was a single stale string, and a source-level check
 * would not have caught it.
 */
export function isFlowsListPath(pathname: string): boolean {
  // A trailing slash is the same route; the query string never reaches
  // `location.pathname`, so nothing needs stripping there.
  return FLOWS_LIST_PATHS.includes(pathname.replace(/\/+$/, '') || '/');
}

/** Are we on the automations list, with its Create button ready? */
const onFlowsListPage = () =>
  isFlowsListPath(window.location.pathname) && Boolean(resolveAnchor(CREATE_AUTOMATION_BUTTON));

/** Is the "name your automation" dialog on screen? */
const nameDialogOpen = () => Boolean(findVisible(NAME_INPUT_SELECTOR));

/** Are we on a flow's own canvas (list route excluded — it has no `:id` segment)? */
const onFlowBuilderCanvas = () =>
  /^\/home\/automation\/flows\/[^/]+$/.test(window.location.pathname) &&
  Boolean(document.querySelector('.react-flow'));

/**
 * Is this canvas one that has NEVER been saved?
 *
 * `Flows.confirmCreateFlow` navigates to the literal `/flows/new`; the record
 * itself is only created on Save, and `FlowBuilder` then replaces the url with
 * the new id. So this path means "nothing exists on the server yet", which is
 * the whole basis for the discard decision in `afterEnd` below.
 */
const onUnsavedNewFlow = () => window.location.pathname === '/home/automation/flows/new';

const two = (n: number) => String(n).padStart(2, '0');

/**
 * Resolved ONCE, when the module loads — see the identical pattern (and the
 * reason for it) on `MESSAGE_TEMPLATE_TASK`'s `SUGGESTED_NAME`.
 */
function suggestedFlowName(): string {
  const now = new Date();
  return `Welcome flow ${two(now.getHours())}${two(now.getMinutes())}${two(now.getSeconds())}`;
}
const SUGGESTED_NAME = suggestedFlowName();

/** Does the canvas hold a node of this type? React Flow classes every node `.react-flow__node-{type}`. */
function hasNodeOfType(nodeType: string): boolean {
  return Boolean(document.querySelector(`.react-flow__node-${nodeType}`));
}

function edgeCount(): number {
  return document.querySelectorAll('.react-flow__edge').length;
}

function sourceHandleOf(nodeType: string): HTMLElement | null {
  return document.querySelector(`.react-flow__node-${nodeType} .react-flow__handle.source`);
}

function targetHandleOf(nodeType: string): HTMLElement | null {
  return document.querySelector(`.react-flow__node-${nodeType} .react-flow__handle.target`);
}

/**
 * Edge-count baselines for the two "connect" steps, captured by their own
 * `prepare()` on entry. Module-scoped (rather than counting from zero) because
 * a step is re-entered on Back, and by then the OTHER connection may already
 * exist — `satisfied()` has to mean "this step's own wire appeared", not
 * "there is at least one edge anywhere on the canvas".
 */
let startToMessageBaseline = 0;
let messageToEndBaseline = 0;

/**
 * Drop a palette node onto the canvas without a real mouse drag.
 *
 * Carries the same two `dataTransfer` keys the palette's own `onDragStart`
 * sets (`application/reactflow`, `application/reactflow-label`), dispatched
 * as a `drop` DragEvent on the canvas root. Where on the canvas it lands does
 * not matter — dropped nodes are placed in a fixed column below the lowest
 * existing node regardless of drop coordinates (see `FlowBuilder.tsx`'s
 * `onDrop`), not at the cursor.
 */
function dropNodeOntoCanvas(nodeType: string, label: string): boolean {
  const canvas = document.querySelector('.react-flow');
  if (!canvas) return false;
  try {
    const dataTransfer = new DataTransfer();
    dataTransfer.setData('application/reactflow', nodeType);
    dataTransfer.setData('application/reactflow-label', label);
    const rect = canvas.getBoundingClientRect();
    const clientX = rect.left + rect.width / 2;
    const clientY = rect.top + rect.height / 2;
    const init: DragEventInit = { bubbles: true, cancelable: true, dataTransfer, clientX, clientY };
    canvas.dispatchEvent(new DragEvent('dragover', init));
    canvas.dispatchEvent(new DragEvent('drop', init));
    return true;
  } catch {
    return false;
  }
}

/**
 * Connect two node handles without a real mouse drag.
 *
 * 🔴 MOUSE EVENTS, NOT POINTER EVENTS — verified against the installed
 * `@xyflow/react` (v12) source, not assumed. A Handle's connection gesture is
 * wired as `onMouseDown`/`onTouchStart` (`@xyflow/react/dist/esm/index.js`,
 * the `Handle` component), and starting a connection then adds *native*
 * `mousemove`/`mouseup` listeners on the document (`@xyflow/system`'s
 * `XYHandle.onPointerDown` — the name is legacy, the implementation is not
 * pointer-event-based). A `PointerEvent('pointerdown', …)` dispatch — the
 * first version of this file used one — never reaches an `onMouseDown`
 * handler at all: they are two independent event types that only coincide
 * for real, trusted user input, not for a `dispatchEvent()` call. That was
 * the actual bug behind "the tour explains the wire but never draws it" —
 * `satisfied()` never lied, the gesture simply never started.
 *
 * The library resolves the drop TARGET geometrically at mouseup
 * (`document.elementFromPoint(x, y)` on the final coordinates), not from the
 * dispatched event's own `target`, which is what makes dispatching on
 * `document` for the move/up steps interchangeable with dispatching on the
 * handle element itself.
 */
async function connectHandles(source: HTMLElement, target: HTMLElement): Promise<void> {
  try {
    const srcRect = source.getBoundingClientRect();
    const tgtRect = target.getBoundingClientRect();
    const srcX = srcRect.left + srcRect.width / 2;
    const srcY = srcRect.top + srcRect.height / 2;
    const tgtX = tgtRect.left + tgtRect.width / 2;
    const tgtY = tgtRect.top + tgtRect.height / 2;
    const fire = (el: EventTarget, type: string, x: number, y: number, buttons: number) => {
      el.dispatchEvent(
        new MouseEvent(type, {
          bubbles: true,
          cancelable: true,
          composed: true,
          view: window,
          button: 0,
          buttons,
          clientX: x,
          clientY: y,
        }),
      );
    };

    fire(source, 'mousedown', srcX, srcY, 1);
    const steps = 4;
    for (let i = 1; i <= steps; i += 1) {
      const x = srcX + ((tgtX - srcX) * i) / steps;
      const y = srcY + ((tgtY - srcY) * i) / steps;
      fire(document, 'mousemove', x, y, 1);
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
    }
    fire(document, 'mousemove', tgtX, tgtY, 1);
    fire(document, 'mouseup', tgtX, tgtY, 0);
  } catch {
    // Best-effort — satisfied() is the honest signal, not this call succeeding.
  }
}

/**
 * Open the "Create automation" menu, choose WhatsApp, and land on the name
 * dialog — the two presses that used to be one.
 *
 * 🔴 `pressWhenReady`, NOT `clickWhenReady`, ON THE TRIGGER. It is a Radix
 * `DropdownMenuTrigger`, which opens from `onPointerDown` and has no click
 * handler at all, so the bare `.click()` this step used to send was an event
 * nothing listened for: the menu never opened, and it is why "Create
 * automation" looked like a button the guide simply failed to press. Verified
 * against the live page — a programmatic `.click()` on that trigger leaves
 * `[role="menuitem"]` count at zero. See `formFill.ts`'s `pointerish`.
 *
 * Each stage is flashed before it is pressed (same treatment `createForm.ts`
 * gives its own menu walk), so the channel choice is something the user
 * SEES being made rather than a dialog that appears out of nowhere.
 *
 * Every failure point logs and returns rather than throwing: `onNext` always
 * advances afterwards (see `TaskEngine.advanceFromNext`), so a miss is an
 * honest "skipped" in the tracker with a named reason in the console, not a
 * silent hang.
 */
async function openChannelMenuAndPickWhatsApp(): Promise<void> {
  // Already past this — the user opened the dialog by hand, or pressed Back and
  // then Next again. Pressing the trigger now would only re-open the menu on
  // top of the dialog.
  if (nameDialogOpen()) return;

  if (!channelMenuOpen()) {
    if (!(await pressWhenReady(CREATE_AUTOMATION_BUTTON))) {
      console.warn('[tour-guide] "create-automation-flow": could not find/press Create automation.');
      return;
    }
    if (!(await waitForAnchor(WHATSAPP_CHANNEL_ITEM, 2500))) {
      console.warn('[tour-guide] "create-automation-flow": the channel menu never opened.');
      return;
    }
  }

  await flashAnchor(WHATSAPP_CHANNEL_ITEM);
  if (!(await pressWhenReady(WHATSAPP_CHANNEL_ITEM))) {
    console.warn('[tour-guide] "create-automation-flow": could not press the WhatsApp channel item.');
    return;
  }

  if (!(await waitUntil(nameDialogOpen, 2500))) {
    console.warn('[tour-guide] "create-automation-flow": the name dialog never opened.');
  }
}

export const AUTOMATION_FLOW_TASK: TaskFlow = {
  id: 'create-automation-flow',
  stepId: 'automations--automations',
  cta: 'Try it: build an automation flow',
  preparingLabel: 'Opening Automations…',
  title: 'Build an automation flow',
  summary: 'Drag on two nodes, connect them, check for errors, save, and head back to your list.',
  doneTitle: 'Automation flow created',
  doneDescription:
    'Start → Send Message → End, connected, checked and saved. Reopen it any time from your automations list to add more steps, then Publish when you are ready to turn it on.',

  /**
   * Get to the automations list BEFORE the flow's own chrome appears — same
   * reasoning as `MESSAGE_TEMPLATE_TASK.beforeStart`: the product tour just
   * finished pointing at this exact sidebar row, so re-highlighting it as the
   * flow's own "step one" would only repeat what the tour already showed.
   */
  beforeStart: async () => {
    // 🔴 WARM BOTH ROUTE CHUNKS BEFORE ANYTHING ELSE, AND DO NOT AWAIT THEM.
    //
    // This is the actual "why is it taking so long" of this flow, and it is
    // not the sandbox: `/home/automation/flows` and `/home/automation/flows/:id`
    // are two SEPARATE `lazy()` chunks in `App.tsx`, and the builder's one
    // carries `@xyflow/react` — by far the heaviest single dependency any stop
    // on this tour pulls in. Left alone they download STRICTLY IN SERIES: the
    // list chunk starts on the sidebar click, and the builder chunk does not
    // start until `confirm-create` navigates, several seconds later, while the
    // user sits on a "Opening the builder…" hint watching nothing happen.
    //
    // Kicking both off here overlaps the builder's download with the two
    // callouts the user is reading in between, so by the time `confirm-create`
    // runs the module is usually already in memory and the canvas appears at
    // once. `import()` is memoised by the module system, so the real navigation
    // reuses this exact promise rather than fetching twice.
    //
    // 🔴 NOT AWAITED. Awaiting a chunk on a slow connection would stall the
    // sidebar click behind it — trading the lag at step 3 for the same lag at
    // step 0, which is worse (nothing is on screen yet to explain it). A
    // rejection is swallowed for the same reason: a warm-up that fails must be
    // invisible, because the ordinary route load will simply do it again.
    void import('@/pages/automation/Flows').catch(() => {});
    void import('@/pages/automation/FlowBuilder').catch(() => {});

    if (onFlowsListPage()) return;
    openAccordion(['Automations']);
    await clickWhenReady(SIDEBAR_ROW);
    const arrived = await waitUntil(onFlowsListPage, 5000);
    if (!arrived) {
      console.warn(
        '[tour-guide] "create-automation-flow": never confirmed arrival on the automations list ' +
          '(route and/or Create automation button). The next step may resolve the wrong button.',
      );
    }
  },

  /**
   * Put the app back where the tour can carry on — see `TaskFlow.afterEnd`.
   *
   * 🔴 THIS IS WHAT "SKIP MODULE DOES NOT CLOSE THE CREATION PAGE" WAS. The
   * engine's teardown removes the ring, the callout and the bar; it does not
   * know this flow opened a modal to name the record and then navigated to a
   * builder. So the tour resumed with the name dialog still up — and a Radix
   * modal takes the whole screen with it: `pointer-events: none` on the body,
   * `aria-hidden` on everything outside itself, and a dark backdrop over the
   * page. The next module's highlight was drawn UNDER that backdrop, which is
   * both halves of the report: the module not shown correctly, and a background
   * that never went back to normal.
   *
   * 🔴 THE DISCARD IS GATED ON `onUnsavedNewFlow()`, AND THAT GATE IS THE WHOLE
   * SAFETY ARGUMENT. Leaving the builder routes through the app's own
   * unsaved-changes guard, whose confirm offers "Discard changes". Pressing
   * that for the user is only ever acceptable when there is genuinely nothing
   * to lose:
   *
   *  - `/flows/new` — the record has never been created (`confirmCreateFlow`
   *    navigates there and only Save creates it), and every node on that canvas
   *    was placed by THIS GUIDE, not by the user. Discarding it throws away the
   *    guide's own work, so it leaves.
   *  - `/flows/<id>` — a real record exists and the unsaved changes are edits to
   *    it. Those belong to the user, so the guard is dismissed with "Keep
   *    editing" and the flow stays put. The dialog is closed either way, which
   *    is what actually restores the page.
   *
   * A saved-then-skipped run hits neither branch: there is nothing unsaved, so
   * the back arrow navigates straight to the list with no confirm at all.
   *
   * Idempotent throughout — this also runs on a FINISHED flow, which has already
   * walked itself back to the list and finds nothing left to do.
   */
  afterEnd: async () => {
    // The name dialog. Nothing has been created behind it yet, so simply closing
    // it is the whole of the cleanup — through the shared helper rather than one
    // hand-rolled click, so it gets the same retry, Escape fallback and warning
    // the other three flows get. `closeFlowChrome` is a no-op when the dialog was
    // never open, which is every exit from the builder onwards.
    await closeFlowChrome(nameDialogOpen);

    if (!onFlowBuilderCanvas()) return;

    const neverSaved = onUnsavedNewFlow();
    if (!(await clickWhenReady(BACK_TO_LIST_BUTTON, 1500))) return;

    // The guard only appears when the builder reports unsaved changes, so a
    // clean canvas never gets this far — hence the short wait rather than a
    // long one, and no warning when it does not come.
    const guardAsked = await waitUntil(
      () => Boolean(resolveAnchor(DISCARD_CHANGES_BUTTON)),
      1200,
    );
    if (guardAsked) {
      await clickWhenReady(neverSaved ? DISCARD_CHANGES_BUTTON : KEEP_EDITING_BUTTON, 1500);
    }

    // Only meaningful on the branch that actually leaves; the "keep editing"
    // branch simply runs this out, which costs the tour nothing (the resume is
    // already racing its own timeout).
    if (neverSaved || !guardAsked) await waitUntil(onFlowsListPage, 3000);
  },

  steps: [
    {
      id: 'open-composer',
      label: 'New flow',
      title: 'Start a new automation',
      description:
        'This is your automation list — every flow you have built, and whether it is live. A flow reacts to a trigger (a keyword, a form submission, a schedule) and then does something automatically: send a message, wait, branch, or hand off to a person. Create automation asks which channel first — press Next and I will pick WhatsApp.',
      anchor: CREATE_AUTOMATION_BUTTON,
      side: 'bottom',
      spotlight: true,
      ready: onFlowsListPage,
      prepare: async () => {},
      onNext: openChannelMenuAndPickWhatsApp,
      satisfied: nameDialogOpen,
      /**
       * Back over this step closes what its Next opened — see `TaskStep.rewind`.
       *
       * Without it, Back from the step after this one landed HERE with the
       * name dialog still covering the Create automation
       * button this step is about, which `resolve()` then refuses to ring: a step on screen with nothing
       * highlighted, which is what "Back disturbs the tour" looks like.
       */
      rewind: async () => {
        await closeFlowChrome(nameDialogOpen, 'rewind');
      },
      waitingHint: 'Press Next to start a new automation.',
    },

    {
      id: 'name-flow',
      label: 'Name it',
      title: 'Name your flow',
      description:
        'Filled in for you — every flow needs a name so you can find it again in your list later. You can rename it any time from the builder.',
      anchor: { selector: NAME_INPUT_SELECTOR },
      side: 'right',
      spotlight: true,
      fill: [[NAME_INPUT_SELECTOR, SUGGESTED_NAME]],
      satisfied: () => isFilled(NAME_INPUT_SELECTOR),
      waitingHint: 'Type a name to carry on.',
    },

    {
      id: 'confirm-create',
      label: 'Create',
      title: 'Open the builder',
      description:
        'Create flow opens the canvas. Every new flow starts with a Starting Step already on it — that is where a contact enters the automation.',
      anchor: CONFIRM_CREATE_BUTTON,
      side: 'top',
      spotlight: true,
      advance: 'auto',
      prepare: async () => {
        if (onFlowBuilderCanvas()) return;
        await clickWhenReady(CONFIRM_CREATE_BUTTON);
      },
      satisfied: onFlowBuilderCanvas,
      waitingHint: 'Opening the builder…',
    },

    {
      id: 'meet-start',
      label: 'Start',
      title: 'The Starting Step',
      description:
        'Every flow needs exactly one entry point — this is it. It fires when a contact\'s message matches its trigger (a keyword, right now). Next, drag a couple of steps onto the canvas so it actually does something.',
      anchor: { selector: '.react-flow__node-starting_step' },
      side: 'right',
      spotlight: true,
      // Already true the instant this step is entered (the node ships on every
      // new canvas) — force the ring to show anyway rather than silently
      // skipping past the one node the rest of this flow builds on.
      forceHighlight: true,
      satisfied: () => hasNodeOfType('starting_step'),
    },

    {
      id: 'add-send-message',
      label: 'Send Message',
      title: 'Add a Send Message step',
      description:
        'Drag "Send Message" from the panel on the left onto the canvas. It sends the contact a WhatsApp message the moment the flow reaches it — the most common thing an automation does. Press Next and I will drop it on for you, or drag it yourself.',
      anchor: draggableLabelled('Send Message'),
      side: 'right',
      spotlight: true,
      ready: onFlowBuilderCanvas,
      prepare: async () => {},
      onNext: async () => {
        if (hasNodeOfType('send_message')) return;
        dropNodeOntoCanvas('send_message', 'Send Message');
        await waitUntil(() => hasNodeOfType('send_message'), 1500);
      },
      satisfied: () => hasNodeOfType('send_message'),
      waitingHint: 'Drag "Send Message" onto the canvas, or press Next to add it for you.',
    },

    /**
     * Dropping a node auto-opens its inspector (`FlowBuilder.tsx`'s `onDrop`
     * calls `setSelectedNode`), so the message field is already on screen the
     * instant this step is entered. Filling it here — rather than leaving it
     * blank and reacting to the validation error later — is what stops
     * "Send Message fails Run test" from happening at all: an empty body is
     * the ONE thing this node type is flagged for (`FlowValidationPanel`'s
     * `case 'send_message'` check), and every other node this flow builds
     * (Start, End) has nothing else required.
     */
    {
      id: 'fill-message',
      label: 'Message text',
      title: 'Write the message',
      description:
        'Filled in for you — a Send Message node needs body text, or Run test flags it as an error. Edit it if you like, or leave it and press Next.',
      anchor: { selector: MESSAGE_FIELD_SELECTOR },
      side: 'left',
      spotlight: true,
      ready: () => onFlowBuilderCanvas() && hasNodeOfType('send_message'),
      fill: [[MESSAGE_FIELD_SELECTOR, DUMMY_MESSAGE]],
      satisfied: () => isFilled(MESSAGE_FIELD_SELECTOR),
      waitingHint: 'Type a message to carry on.',
      // The inspector not being open (the user closed it, or focus moved
      // elsewhere between steps) is recoverable later — `run-test`'s own
      // onNext fills this same field again if Run test ends up flagging it.
      optional: true,
    },

    /**
     * Connect Start → Send Message right after Send Message lands, rather
     * than dropping both nodes first and wiring them after — one node, one
     * wire, then the next node, so the flow reads as a single continuous
     * chain instead of two separate passes.
     */
    {
      id: 'connect-start-to-message',
      label: 'Connect Start',
      title: 'Connect Start to Send Message',
      description:
        'Drag from the small dot at the bottom of Starting Step to the dot at the top of Send Message. This wire is what tells the flow what happens next — nothing downstream of a node runs until it is actually connected. Press Next and I will connect them for you, or drag the wire yourself.',
      anchor: { selector: '.react-flow__node-starting_step .react-flow__handle.source' },
      side: 'right',
      spotlight: true,
      ready: () => onFlowBuilderCanvas() && hasNodeOfType('send_message'),
      prepare: async () => {
        startToMessageBaseline = edgeCount();
      },
      onNext: async () => {
        if (edgeCount() > startToMessageBaseline) return;
        const source = sourceHandleOf('starting_step');
        const target = targetHandleOf('send_message');
        if (source && target) {
          await connectHandles(source, target);
          await waitUntil(() => edgeCount() > startToMessageBaseline, 1500);
        }
      },
      satisfied: () => edgeCount() > startToMessageBaseline,
      waitingHint: 'Drag the connection yourself, or press Next and I will connect them.',
      // A missing Send Message node (the previous step's drop failed and the
      // user did not do it by hand either) means there is nothing to wire —
      // skip rather than stall on a target that will never appear.
      optional: true,
    },

    {
      id: 'add-end',
      label: 'End',
      title: 'Add an End step',
      description:
        'Now drag "End" onto the canvas, underneath Send Message. It marks where this branch of the flow finishes — every flow should end somewhere explicit rather than trailing off. Press Next and I will drop it on for you, or drag it yourself.',
      anchor: draggableLabelled('End'),
      side: 'right',
      spotlight: true,
      ready: onFlowBuilderCanvas,
      prepare: async () => {},
      onNext: async () => {
        if (hasNodeOfType('end')) return;
        dropNodeOntoCanvas('end', 'End');
        await waitUntil(() => hasNodeOfType('end'), 1500);
      },
      satisfied: () => hasNodeOfType('end'),
      waitingHint: 'Drag "End" onto the canvas, or press Next to add it for you.',
    },

    {
      id: 'connect-message-to-end',
      label: 'Connect End',
      title: 'Connect Send Message to End',
      description:
        'Same gesture, one more time: drag from the dot at the bottom of Send Message to the dot at the top of End. That completes the flow — a message goes out, then the flow ends.',
      anchor: { selector: '.react-flow__node-send_message .react-flow__handle.source' },
      side: 'right',
      spotlight: true,
      ready: () => onFlowBuilderCanvas() && hasNodeOfType('end'),
      prepare: async () => {
        messageToEndBaseline = edgeCount();
      },
      onNext: async () => {
        if (edgeCount() > messageToEndBaseline) return;
        const source = sourceHandleOf('send_message');
        const target = targetHandleOf('end');
        if (source && target) {
          await connectHandles(source, target);
          await waitUntil(() => edgeCount() > messageToEndBaseline, 1500);
        }
      },
      satisfied: () => edgeCount() > messageToEndBaseline,
      waitingHint: 'Drag the connection yourself, or press Next and I will connect them.',
      optional: true,
    },

    {
      id: 'run-test',
      label: 'Check errors',
      title: 'Check for errors',
      description:
        'Click Run test. It opens a panel listing anything the flow is missing before it can go live — a broken connection, a node with nothing wired into it, an empty message. If it finds something, I will open that node and jump straight to the field causing it.',
      anchor: RUN_TEST_BUTTON,
      side: 'bottom',
      spotlight: true,
      ready: onFlowBuilderCanvas,
      prepare: async () => {},
      /**
       * Self-healing, not just informational: after opening the panel, if it
       * lists anything, click that issue's own "Focus" button (opens the
       * offending node's inspector and pans the canvas to it — see
       * `FlowValidationPanel.onNodeFocus`), then fill the message field if
       * that turns out to be what was missing.
       *
       * 🔴 EVERY WAIT HERE IS SHORT AND LOCAL TO THIS `onNext`, NOT THE
       * ENGINE'S OWN STEP-LEVEL ANCHOR RESOLUTION. The panel legitimately
       * reports "No Issues" once `fill-message` has already done its job —
       * that is the expected common case, not a failure — and a step whose
       * OWN `anchor` were `FIRST_ISSUE_FOCUS_BUTTON` would then wait out the
       * engine's full 5s resolve timeout before skipping, every single run.
       * Bounding the wait inside `onNext` instead means a clean flow costs
       * nothing here at all.
       */
      onNext: async () => {
        await clickWhenReady(RUN_TEST_BUTTON);
        // Short: the panel's issue list is a synchronous re-render off
        // already-computed validation, not a network fetch — this only needs
        // to survive a couple of paint frames, and the common "No Issues"
        // case waits out this whole budget every time, so it should stay small.
        const foundIssue = await waitUntil(() => Boolean(resolveAnchor(FIRST_ISSUE_FOCUS_BUTTON)), 400);
        if (!foundIssue) return;
        await clickWhenReady(FIRST_ISSUE_FOCUS_BUTTON);
        const fieldOpened = await waitUntil(() => Boolean(findVisible(MESSAGE_FIELD_SELECTOR)), 800);
        if (fieldOpened && !isFilled(MESSAGE_FIELD_SELECTOR)) {
          fillField(MESSAGE_FIELD_SELECTOR, DUMMY_MESSAGE);
        }
      },
      waitingHint: 'Press Next to check the flow for errors.',
      // Not every flow renders a Run test button in the same place (or at
      // all, depending on validation state) — a step that cannot find it
      // should not block Save behind it.
      optional: true,
    },

    {
      id: 'save',
      label: 'Save',
      title: 'Save your flow',
      description:
        'Click Save. It is stored as a draft you can keep editing — nothing goes live until you press Publish separately, so there is no risk in saving early and often.',
      anchor: SAVE_BUTTON,
      side: 'bottom',
      spotlight: true,
      ready: onFlowBuilderCanvas,
      prepare: async () => {},
      onNext: async () => {
        await clickWhenReady(SAVE_BUTTON);
      },
      waitingHint: 'Press Next to save your flow.',
    },

    /**
     * 🔴 THE CANVAS IS FULL-SCREEN AND CHROME-LESS (`AppShell.tsx`'s
     * `isFullscreenBuilder`) — no sidebar, no header. Finishing the task here
     * and leaving it at that stranded the user exactly there: `endTask`
     * resuming/advancing the TOUR moves its own internal step just fine, but
     * the popover for whatever comes next (e.g. the "Forms" sidebar row) has
     * nothing to attach to while the sidebar rendering it is hidden — so nothing
     * visibly happened and the tour read as having stopped dead on this page.
     * Navigating back to the automations list before the task ends is what
     * restores the normal chrome so the NEXT stop actually has something to
     * point at.
     */
    {
      id: 'back-to-list',
      label: 'Back to list',
      title: 'Back to your automations list',
      description:
        'Click the back arrow. Your flow is already saved as a draft — you can reopen it any time from the list to keep building, or hit Publish when it is ready to go live.',
      anchor: BACK_TO_LIST_BUTTON,
      side: 'bottom',
      spotlight: true,
      ready: onFlowBuilderCanvas,
      prepare: async () => {},
      onNext: async () => {
        if (onFlowsListPage()) return;
        await clickWhenReady(BACK_TO_LIST_BUTTON);
      },
      satisfied: onFlowsListPage,
      waitingHint: 'Press Next to return to your automations list.',
    },
  ],
};
